/**
 * Sélecteur INTERNE de SKILLCARDS (jamais le catalogue du site) : les cartes des packs
 * publiés, rangées par pack, avec en tête « Tes dernières SKILLCARDS » (celles que
 * l'élève a travaillées le plus récemment), une recherche sans accents et un filtre en
 * puces (matières). Choix unique ou multiple.
 *
 * Utilisé par le défi privé (portée « SKILLCARDS » / « Exercice ») et par « Ajouter des
 * SKILLCARDS » du tableau de bord élève / joueur (`SkillcardBrowserModal`, qui ouvre la
 * page de la carte dans l'app).
 */

import { useState, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { MathText } from '@jeraisonne/ui';
import { CARD_RARITIES, CARD_RARITY_STARS, stripMath, type CardRarity } from '@jeraisonne/db/cards';
import { PickerModal, PickerOption, type PickerChip } from '@/components/pickers/picker-modal';

export interface CatalogCard {
  id: string;
  title: string;
  rarity?: string | null;
}

export interface CatalogSection {
  key: string;
  label: string;
  cards: CatalogCard[];
}

/** Recherche sans accents dans le titre (LaTeX retiré), plusieurs mots = ET. PUR. */
export function matchesCardQuery(title: string, query: string): boolean {
  const fold = (s: string) =>
    s
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  const words = fold(query).split(/\s+/).filter(Boolean);
  const hay = fold(stripMath(title));
  return words.every((w) => hay.includes(w));
}

/** Cartes des sections filtrées par la recherche ; les sections vides disparaissent. PUR. */
export function filterCatalogSections(
  sections: readonly CatalogSection[],
  query: string,
): CatalogSection[] {
  return sections
    .map((s) => ({ ...s, cards: s.cards.filter((c) => matchesCardQuery(c.title, query)) }))
    .filter((s) => s.cards.length > 0);
}

/**
 * « Tes dernières SKILLCARDS » : les cartes travaillées, de la plus récente à la plus
 * ancienne, limitées à celles que propose le sélecteur (`available`). PUR.
 */
export function recentCatalogCards(
  mine: readonly {
    id: string;
    title: string;
    rarity?: string | null;
    last_used_at: string | null;
  }[],
  available: ReadonlySet<string> | null,
  limit = 5,
): CatalogCard[] {
  return mine
    .filter((c) => c.last_used_at && (!available || available.has(c.id)))
    .sort((a, b) => (b.last_used_at ?? '').localeCompare(a.last_used_at ?? ''))
    .slice(0, limit)
    .map((c) => ({ id: c.id, title: c.title, rarity: c.rarity ?? null }));
}

/** « 3 SKILLCARDS choisies ». PUR. */
export function cardCountLabel(n: number): string {
  if (n === 0) return 'Aucune SKILLCARD choisie';
  return `${n} SKILLCARD${n > 1 ? 'S' : ''} choisie${n > 1 ? 's' : ''}`;
}

function stars(rarity: string | null | undefined): string {
  if (!rarity || !(CARD_RARITIES as readonly string[]).includes(rarity)) return '';
  return '★'.repeat(CARD_RARITY_STARS[rarity as CardRarity]);
}

export const RECENT_SECTION_LABEL = 'Tes dernières SKILLCARDS';

export function SkillcardCatalogModal({
  title,
  sections,
  recent = [],
  multiple = false,
  selected,
  onToggle,
  onClose,
  max,
  chips,
  chipValue,
  onChip,
  allChipsLabel,
  header,
  loading = false,
  emptyLabel = 'Aucune SKILLCARD pour ce niveau et cette matière.',
}: {
  title: string;
  sections: readonly CatalogSection[];
  /** Cartes récentes de l'élève, en tête (masquées pendant une recherche si aucune ne correspond). */
  recent?: readonly CatalogCard[];
  multiple?: boolean;
  selected: readonly string[];
  onToggle: (id: string) => void;
  onClose: () => void;
  /** Choix multiple : nombre maximal (affiché au pied). */
  max?: number;
  chips?: PickerChip[];
  chipValue?: string;
  onChip?: (v: string) => void;
  allChipsLabel?: string;
  /** Contenu au-dessus de la liste (ex. choix du niveau). */
  header?: ReactNode;
  loading?: boolean;
  emptyLabel?: string;
}) {
  const [query, setQuery] = useState('');
  const recentSection: CatalogSection[] =
    recent.length > 0 ? [{ key: 'recent', label: RECENT_SECTION_LABEL, cards: [...recent] }] : [];
  const shown = filterCatalogSections([...recentSection, ...sections], query);
  const full = multiple && max != null && selected.length >= max;
  return (
    <PickerModal
      title={title}
      query={query}
      onQuery={setQuery}
      searchPlaceholder="Rechercher une SKILLCARD…"
      chips={chips}
      chipValue={chipValue}
      onChip={onChip}
      allChipsLabel={allChipsLabel}
      onClose={onClose}
      footer={
        multiple ? (
          <div className="flex items-center justify-between gap-3">
            <span className="text-ink-600 text-sm">
              {cardCountLabel(selected.length)}
              {full ? ` (${max} au plus)` : ''}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="bg-primary-500 min-h-11 rounded-xl px-5 text-sm font-semibold text-white active:scale-[0.98]"
            >
              Valider
            </button>
          </div>
        ) : undefined
      }
    >
      {header}
      <div
        role="listbox"
        aria-multiselectable={multiple || undefined}
        aria-label="SKILLCARDS"
        className="space-y-1"
      >
        {shown.map((sec) => (
          <div key={sec.key} role="group" aria-label={sec.label} className="space-y-1">
            <p className="text-ink-500 px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide">
              <MathText>{sec.label}</MathText>
            </p>
            {sec.cards.map((c) => {
              const on = selected.includes(c.id);
              const s = stars(c.rarity);
              return (
                <PickerOption
                  key={`${sec.key}:${c.id}`}
                  label={stripMath(c.title)}
                  selected={on}
                  onSelect={() => onToggle(c.id)}
                >
                  <span className="text-ink-900 min-w-0 flex-1 text-sm font-semibold">
                    <MathText>{c.title}</MathText>
                  </span>
                  {s && (
                    <span className="text-accent-500 shrink-0 text-xs tracking-tight" aria-hidden>
                      {s}
                    </span>
                  )}
                  {on && <Check className="text-primary-600 h-5 w-5 shrink-0" aria-hidden />}
                </PickerOption>
              );
            })}
          </div>
        ))}
        {loading && shown.length === 0 && (
          <p className="text-ink-500 px-3 py-8 text-center text-sm">Chargement des SKILLCARDS…</p>
        )}
        {!loading && shown.length === 0 && (
          <p className="text-ink-500 px-3 py-8 text-center text-sm">
            {sections.length === 0 ? emptyLabel : 'Aucune SKILLCARD ne correspond.'}
          </p>
        )}
      </div>
    </PickerModal>
  );
}
